// Utility formatting functions

export function formatPhone(raw) {
  if (!raw) return '';
  const digits = String(raw).replace(/\D+/g, '');
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  if (digits.length === 11 && digits.startsWith('1')) {
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  return raw; // fallback unchanged
}

export function formatEmail(raw) {
  if (!raw) return '';
  return String(raw).trim().toLowerCase();
}

export function formatAddress(raw) {
  if (!raw) return '';
  return String(raw).trim().replace(/\s+/g, ' ');
}

export function formatCurrency(amount, decimals = 0) {
  if (amount == null || amount === '') return '';
  const num = Number(amount) || 0;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(num);
}

/**
 * Safely sum numeric values from an array of objects
 * Prevents string concatenation bugs (learned from hub signal #10)
 * @param {Array} array - Array to sum
 * @param {string} [key] - Optional key to extract from each object
 * @returns {number} Sum of values
 */
export function safeSum(array, key) {
  if (!Array.isArray(array)) return 0;
  return array.reduce((sum, item) => {
    const value = key ? item?.[key] : item;
    return sum + (Number(value) || 0);
  }, 0);
}

/**
 * Format a number as percentage
 * @param {number|string} value - Value to format
 * @param {number} [decimals=0] - Decimal places
 * @returns {string} Formatted percentage
 */
export function formatPercent(value, decimals = 0) {
  const num = Number(value) || 0;
  return `${num.toFixed(decimals)}%`;
}

/**
 * Format large numbers with K/M/B suffixes
 * @param {number|string} value - Value to format
 * @returns {string} Compact number (e.g., "1.2M")
 */
export function formatCompact(value) {
  const num = Number(value) || 0;
  return new Intl.NumberFormat('en-US', {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(num);
}

/**
 * Safely parse a number from any input
 * @param {any} value - Value to parse
 * @param {number} [defaultValue=0] - Default if parsing fails
 * @returns {number} Parsed number or default
 */
export function toNumber(value, defaultValue = 0) {
  const num = Number(value);
  return isNaN(num) ? defaultValue : num;
}
